// src/pages/StudentDetail.tsx
import React from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { ref, get, child, push, onValue, update } from "firebase/database";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { CATALOG_PROCEDURES_FIXED } from "../catalog.fixed";

// ---------- name fixing helpers ----------
const ACRONYMS = new Set(["ABG", "COPD", "DLCO", "IV"]);
const legacyToDisplay = (() => {
  const m: Record<string, string> = {};
  const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");
  CATALOG_PROCEDURES_FIXED.forEach((p) => {
    m[norm(p.displayName)] = p.displayName;
    (p.legacyAliases || []).forEach(
      (alias) => (m[norm(alias)] = p.displayName)
    );
  });
  return { map: m, norm };
})();

function titleizeWord(w: string) {
  if (ACRONYMS.has(w.toUpperCase())) return w.toUpperCase();
  return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}
function humanize(raw: string) {
  if (!raw) return raw;
  let s = raw.replace(/[_-]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2");
  s = s.replace(/\s+/g, " ").trim();
  return s
    .split(" ")
    .map((w) => titleizeWord(w))
    .join(" ");
}
function prettyProcName(raw: string) {
  const key = legacyToDisplay.norm(raw);
  return legacyToDisplay.map[key] || humanize(raw);
}

// ---------- types ----------
type PersonalData = {
  userName?: string;
  userEmail?: string;
};

type ProcedureRow = {
  name: string; // raw key from DB
  peakPercentage?: number;
  noOfAttempts?: number;
  attemptsCount: number;
  last10Percentage: number[];
  dataPoints: Array<{ i: number; v: number }>;
  trend: {
    label: "Improving" | "Steady" | "Declining" | "Not enough data";
    last3?: number;
    prev3?: number;
    delta?: number;
  };
};

type NoteItem = { id: string; at?: string; text?: string; by?: string };

// ---------- theme helpers ----------
function getTheme(): "light" | "dark" {
  return (localStorage.getItem("theme") as "light" | "dark") || "dark";
}
function setTheme(t: "light" | "dark") {
  localStorage.setItem("theme", t);
  if (typeof document !== "undefined") {
    document.body.classList.toggle("light-theme", t === "light");
  }
}

// ---------- component ----------
export default function StudentDetail() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const loc = useLocation() as any;
  const college: string =
    loc?.state?.college ||
    (sessionStorage.getItem("college") ?? "UNKNOWN_COLLEGE");

  const [theme, setThemeState] = React.useState<"light" | "dark">(getTheme());
  const [personal, setPersonal] = React.useState<PersonalData | null>(null);
  const [section, setSection] = React.useState<"clinical" | "osce" | "quiz">(
    "clinical"
  );
  const [procedures, setProcedures] = React.useState<ProcedureRow[]>([]);
  const [osce, setOsce] = React.useState<{
    semester: string;
    dateTime?: string;
    stations?: Record<string, boolean>;
  } | null>(null);
  const [quizzes, setQuizzes] = React.useState<
    Array<{
      subject: string;
      averageScore?: number;
      quizes?: Record<string, number>;
      sectionName?: string;
    }>
  >([]);
  const [notes, setNotes] = React.useState<NoteItem[]>([]);
  const [newNote, setNewNote] = React.useState("");

  React.useEffect(() => {
    if (!uid) {
      navigate("/f");
      return;
    }
    if (college) sessionStorage.setItem("college", college);

    (async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login");
        return;
      }
      const root = ref(db);
      const base = `Colleges/${college}/Users/${uid}`;

      // Personal Data
      const pdSnap = await get(child(root, `${base}/Personal Data`));
      const pd = pdSnap.exists() ? (pdSnap.val() as PersonalData) : {};
      setPersonal(pd);

      // Game Modes (unchanged structure)
      const gmSnap = await get(child(root, `${base}/Game Modes`));
      if (gmSnap.exists()) {
        const items: any = gmSnap.val();
        const arr: ProcedureRow[] = Object.keys(items).map((k) => {
          const raw = items[k]?.last10Percentage ?? [];
          const last10 = normalizeNumArray(raw);
          const attemptsCount = items[k]?.noOfAttempts ?? last10.length;
          const dataPoints = last10.map((v: number, idx: number) => ({
            i: idx + 1,
            v,
          }));
          return {
            name: k,
            peakPercentage: items[k]?.peakPercentage ?? undefined,
            noOfAttempts: items[k]?.noOfAttempts ?? undefined,
            attemptsCount,
            last10Percentage: last10,
            dataPoints,
            trend: computeTrend(last10),
          };
        });
        setProcedures(arr);
      } else {
        setProcedures([]);
      }

      // OSCE
      const osceSnap = await get(child(root, `${base}/OSCE Data`));
      if (osceSnap.exists()) {
        const obj: Record<string, any> = osceSnap.val();
        const latestSemKey = pickLatestSemesterKey(obj);
        if (latestSemKey) {
          setOsce({
            semester: latestSemKey,
            dateTime: obj[latestSemKey]?.dateTime,
            stations: obj[latestSemKey]?.stations || {},
          });
        } else setOsce(null);
      } else setOsce(null);

      // Quizzes
      const qSnap = await get(child(root, `${base}/Quizes Data`));
      if (qSnap.exists()) {
        const q: Record<string, any> = qSnap.val();
        const list = Object.keys(q).map((subject) => ({
          subject,
          averageScore: q[subject]?.averageScore,
          quizes: q[subject]?.quizes || {},
          sectionName: q[subject]?.sectionName,
        }));
        setQuizzes(list);
      } else setQuizzes([]);

      // Notes live listener
      const notesRef = ref(db, `${base}/Notes`);
      const unsub = onValue(notesRef, (snap) => {
        const v = snap.val() as Record<string, any> | null;
        if (!v) return setNotes([]);
        const list = Object.entries(v)
          .map(([id, n]) => ({
            id,
            at: n?.at,
            text: n?.text,
            by: n?.by,
          }))
          .sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")));
        setNotes(list);
      });
      return () => unsub();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, college]);

  function toggleTheme() {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    setThemeState(next);
  }

  async function addNote() {
    if (!newNote.trim() || !uid) return;
    const by = auth.currentUser?.email || "unknown";
    const at = new Date().toISOString();
    const base = `Colleges/${college}/Users/${uid}/Notes`;
    const id = push(ref(db, base)).key!;
    await update(ref(db), { [`${base}/${id}`]: { at, by, text: newNote } });
    setNewNote("");
  }

  return (
    <div className="container">
      <div className="row" style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>{personal?.userName || uid}</h1>
        <button className="btn" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      {/* Tabs */}
      <div
        className="card"
        style={{
          marginBottom: 16,
          padding: 8,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span className="small" style={{ opacity: 0.8 }}>
          View:
        </span>
        <button
          className="btn"
          aria-pressed={section === "clinical"}
          onClick={() => setSection("clinical")}
          style={{
            background:
              section === "clinical" ? "rgba(59,130,246,.12)" : "transparent",
            borderColor:
              section === "clinical" ? "rgba(59,130,246,.5)" : "var(--line)",
          }}
        >
          Clinical Procedures
        </button>
        <button
          className="btn"
          aria-pressed={section === "osce"}
          onClick={() => setSection("osce")}
          style={{
            background:
              section === "osce" ? "rgba(59,130,246,.12)" : "transparent",
            borderColor:
              section === "osce" ? "rgba(59,130,246,.5)" : "var(--line)",
          }}
        >
          OSCE
        </button>
        <button
          className="btn"
          aria-pressed={section === "quiz"}
          onClick={() => setSection("quiz")}
          style={{
            background:
              section === "quiz" ? "rgba(59,130,246,.12)" : "transparent",
            borderColor:
              section === "quiz" ? "rgba(59,130,246,.5)" : "var(--line)",
          }}
        >
          Quiz
        </button>
      </div>

      {/* Two columns: Personal + Notes */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(280px, 1fr) 1.2fr",
          gap: 16,
          alignItems: "start",
        }}
      >
        <div className="card">
          <h3>Personal Data</h3>
          <p className="kv">
            <span>Name</span> <b>{personal?.userName || "—"}</b>
          </p>
          <p className="kv">
            <span>Email</span> <b>{personal?.userEmail || "—"}</b>
          </p>
        </div>

        <div className="card">
          <h3>Notes</h3>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <input
              className="input"
              placeholder="Leave a note for this student…"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
            />
            <button className="btn" onClick={addNote}>
              Add
            </button>
          </div>
          {notes.length === 0 ? (
            <p className="small">No notes yet.</p>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {notes.map((n, i) => (
                <div
                  key={n.id}
                  className="card"
                  style={{
                    background: "var(--surface-2)",
                    borderColor: "var(--line)",
                  }}
                >
                  <div className="small" style={{ opacity: 0.8 }}>
                    {n.at || "—"} — {n.by || "—"}
                  </div>
                  <div style={{ marginTop: 4 }}>
                    {i + 1}. {n.text || "—"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Section views */}
      {section === "clinical" && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Clinical Procedures</h3>
          {procedures.length === 0 ? (
            <p className="small">—</p>
          ) : (
            <div style={{ display: "grid", gap: 16 }}>
              {procedures.map((p) => (
                <div
                  key={p.name}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(260px, 360px) 1fr",
                    gap: 16,
                    alignItems: "center",
                    border: "1px solid var(--line)",
                    borderRadius: 12,
                    padding: 12,
                    background: "var(--surface-1)",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 16,
                        marginBottom: 4,
                      }}
                    >
                      {prettyProcName(p.name)}
                    </div>
                    <div className="small" style={{ marginBottom: 6 }}>
                      peak <b>{p.peakPercentage ?? "—"}%</b> • attempts{" "}
                      <b>{p.attemptsCount}</b>
                    </div>

                    <div style={{ marginTop: 6 }}>
                      <div
                        className="small"
                        style={{ opacity: 0.8, marginBottom: 4 }}
                      >
                        Performance overview
                      </div>
                      <span
                        className={`pill ${
                          p.trend.label === "Improving"
                            ? "good"
                            : p.trend.label === "Declining"
                            ? "bad"
                            : p.trend.label === "Steady"
                            ? "warn"
                            : "muted"
                        }`}
                        style={{ marginRight: 8 }}
                      >
                        {p.trend.label}
                      </span>
                      {p.trend.last3 !== undefined &&
                        p.trend.prev3 !== undefined && (
                          <span className="small">
                            {p.trend.delta && p.trend.delta > 0
                              ? "Up"
                              : p.trend.delta && p.trend.delta < 0
                              ? "Down"
                              : "No change"}{" "}
                            <b>{Math.abs(p.trend.delta || 0)} pts</b> (last 3
                            avg <b>{p.trend.last3}%</b> vs previous 3 avg{" "}
                            <b>{p.trend.prev3}%</b>)
                          </span>
                        )}
                      {p.trend.label === "Not enough data" && (
                        <span className="small">
                          {" "}
                          Need at least 6 attempts to assess trend.
                        </span>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      width: "100%",
                      height: 140,
                      background: "var(--surface-2)",
                      border: "1px solid var(--line)",
                      borderRadius: 10,
                      padding: "8px 10px",
                    }}
                  >
                    {p.dataPoints.length ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={p.dataPoints}>
                          <CartesianGrid
                            stroke="rgba(255,255,255,0.08)"
                            strokeDasharray="3 3"
                          />
                          <XAxis
                            dataKey="i"
                            tick={{
                              fontSize: 11,
                              fill: "rgba(255,255,255,.7)",
                            }}
                            label={{
                              value: "Attempt",
                              position: "insideBottomRight",
                              offset: -4,
                              fill: "rgba(255,255,255,.8)",
                              fontSize: 11,
                            }}
                            allowDecimals={false}
                          />
                          <YAxis
                            domain={[0, 100]}
                            tick={{
                              fontSize: 11,
                              fill: "rgba(255,255,255,.7)",
                            }}
                            label={{
                              value: "Score (%)",
                              angle: -90,
                              position: "insideLeft",
                              fill: "rgba(255,255,255,.8)",
                              fontSize: 11,
                            }}
                            allowDecimals={false}
                          />
                          <Tooltip
                            formatter={(value: any) => [`${value}%`, "Score"]}
                            labelFormatter={(label: any) => `Attempt ${label}`}
                            contentStyle={{
                              background: "#0b0f15",
                              border: "1px solid var(--line)",
                            }}
                          />
                          <Line
                            type="monotone"
                            dataKey="v"
                            stroke="#60a5fa"
                            strokeWidth={2}
                            dot={{ r: 2 }}
                            isAnimationActive={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div
                        className="small"
                        style={{ padding: 8, color: "var(--muted)" }}
                      >
                        No attempts yet
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {section === "osce" && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>OSCE (latest)</h3>
          {!osce ? (
            <p className="small">—</p>
          ) : (
            <>
              <p className="kv">
                <span>Semester</span> <b>{osce.semester}</b> &nbsp;
                <span className="small">at {osce.dateTime || "—"}</span>
              </p>
              {osce.stations && Object.keys(osce.stations).length ? (
                <table className="table" style={{ marginTop: 8 }}>
                  <thead>
                    <tr>
                      <th>Station</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(osce.stations).map((k) => (
                      <tr key={k}>
                        <td>{k}</td>
                        <td>{osce.stations![k] ? "✓ Passed" : "✗ Failed"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="small">No station data.</p>
              )}
            </>
          )}
        </div>
      )}

      {section === "quiz" && (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Quizzes</h3>
          {quizzes.length === 0 ? (
            <p className="small">—</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Avg</th>
                  <th>Quizzes</th>
                </tr>
              </thead>
              <tbody>
                {quizzes.map((q) => (
                  <tr key={q.subject}>
                    <td>{q.sectionName || q.subject}</td>
                    <td>
                      <span
                        className="badge"
                        style={{
                          borderColor:
                            (q.averageScore ?? 0) < 0
                              ? "rgba(239,68,68,.5)"
                              : "rgba(59,130,246,.4)",
                          background:
                            (q.averageScore ?? 0) < 0
                              ? "rgba(239,68,68,.08)"
                              : "rgba(59,130,246,.08)",
                        }}
                      >
                        {q.averageScore ?? "—"}
                      </span>
                    </td>
                    <td className="small">
                      {q.quizes && Object.keys(q.quizes).length
                        ? Object.entries(q.quizes)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join("  •  ")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- helpers ----------
function normalizeNumArray(x: any): number[] {
  if (Array.isArray(x))
    return x.map((n) => Number(n)).filter((n) => !Number.isNaN(n));
  if (x && typeof x === "object") {
    return Object.keys(x)
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => Number(x[k]))
      .filter((n) => !Number.isNaN(n));
  }
  return [];
}
function round1(n: number) {
  return Math.round(n * 10) / 10;
}
function avg(nums: number[]): number {
  if (!nums.length) return 0;
  return round1(nums.reduce((a, b) => a + b, 0) / nums.length);
}
function computeTrend(last10: number[]): ProcedureRow["trend"] {
  if (!last10 || last10.length < 6) return { label: "Not enough data" };
  const last3 = avg(last10.slice(-3));
  const prev3 = avg(last10.slice(-6, -3));
  const delta = round1(last3 - prev3);
  if (delta >= 5) return { label: "Improving", last3, prev3, delta };
  if (delta <= -5) return { label: "Declining", last3, prev3, delta };
  return { label: "Steady", last3, prev3, delta };
}
function parseMaybeISO(s?: string): number {
  if (!s) return 0;
  const d = Date.parse(s);
  if (!Number.isNaN(d)) return d;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (!m) return 0;
  const [, yy, mm, dd, hh, mi, ss] = m.map(Number) as any;
  return new Date(yy, mm - 1, dd, hh, mi, ss).getTime();
}
function pickLatestSemesterKey(osceObj: Record<string, any>): string | null {
  const keys = Object.keys(osceObj || {});
  if (!keys.length) return null;
  const withTimes = keys.map((k) => ({
    k,
    t: parseMaybeISO(osceObj[k]?.dateTime),
  }));
  withTimes.sort((a, b) => b.t - a.t);
  if (withTimes[0].t > 0) return withTimes[0].k;
  const withN = keys.map((k) => ({
    k,
    n: Number(String(k).replace(/\D+/g, "")) || 0,
  }));
  withN.sort((a, b) => b.n - a.n);
  return withN[0].k;
}
